/**
 * Traditional Chinese copy for the catalog's English templates. The API catalog stays the source
 * of truth; this only swaps display text when the UI language is zh-TW.
 */

import type {AgentStarterTemplate, TemplateExampleSession} from "./agentTemplates"

interface ConnectionCopy {
    role?: string
    scope?: string
    /** Keyed by the English tool name. */
    tools?: Record<string, {name: string; description: string}>
}

interface TemplateCopy {
    name: string
    description: string
    overview: string
    instructions: string
    trigger: string
    triggerDescription: string
    seedMessage: string
    example?: TemplateExampleSession
    connections?: Record<string, ConnectionCopy>
}

const SLACK_HISTORY = {name: "讀取對話紀錄", description: "讀取頻道近期訊息以了解討論串。"}
const NOTION_SEARCH = {name: "搜尋", description: "找出與問題相關的頁面。"}

const ZH_TW: Record<string, TemplateCopy> = {
    "support-triage": {
        name: "客服分流",
        description: "讀取 #support 頻道，標記急迫性並分派給負責人。",
        overview:
            "監看 #support 頻道，依急迫性分流每則新討論串、分派給適當的負責人，結案前先與您確認。",
        instructions: "監看 #support，依急迫性分流每則新討論串並分派給負責人；結案前先詢問。",
        trigger: "#support 有新訊息",
        triggerDescription: "#support 頻道有新訊息時執行。",
        seedMessage: "建立一個客服分流代理：讀取 #support 的新討論串、標記急迫性並分派給負責人。",
        connections: {
            "read-and-post-in-the-support-channel": {
                role: "讀取並回覆客服頻道",
                scope: "讀取頻道、回覆並分派討論串",
                tools: {
                    "Fetch conversation history": SLACK_HISTORY,
                    "Send message": {
                        name: "傳送訊息",
                        description: "在討論串中回覆並分派給負責人。",
                    },
                },
            },
        },
    },
    "support-reply-drafter": {
        name: "客服回覆草擬",
        description: "依您的文件為新工單草擬回覆。",
        overview: "為每張新客服工單草擬回覆，答案取自您的文件。",
        instructions: "收到新工單時，依文件工作區的內容草擬回覆；送出前留待審閱。",
        trigger: "新工單",
        triggerDescription: "收到新的客服工單時執行。",
        seedMessage: "建立一個代理：依我們的文件為新客服工單草擬回覆。",
        example: {
            prompt: "新工單：「可以把執行結果匯出成 CSV 嗎？」",
            steps: [
                "讀取工單與客戶方案",
                "在文件中找到對應的答案",
                "草擬回覆並保留未送出，等待審閱",
            ],
            reply: "已依您平常的語氣草擬回覆，說明匯出流程。回覆以內部備註留在工單上，確認後再發布。",
            status: "等待核准",
        },
        connections: {
            "read-the-tickets": {
                role: "讀取工單",
                scope: "讀取工單、張貼回覆草稿",
                tools: {
                    "Get ticket": {name: "取得工單", description: "讀取新工單的主旨、內容與歷程。"},
                    "Add comment": {
                        name: "新增備註",
                        description: "把回覆草稿貼成內部備註供審閱。",
                    },
                },
            },
            "read-a-knowledge-source": {role: "讀取知識來源", scope: "讀取知識來源"},
        },
    },
    "lead-qualifier": {
        name: "潛在客戶評估",
        description: "補充並評估新進的潛在客戶。",
        overview: "補充每位新進潛在客戶的資料，依您的條件評估，並加入 HubSpot。",
        instructions: "收到新潛在客戶時，補充資料、評估條件，並建立或更新 HubSpot 紀錄。",
        trigger: "新潛在客戶或來信",
        triggerDescription: "有新的潛在客戶或來信時執行。",
        seedMessage: "建立一個代理：補充每位新進潛在客戶的資料、評估條件，並加入 HubSpot。",
        connections: {
            "write-the-qualified-lead": {
                role: "寫入合格的潛在客戶",
                scope: "讀取與建立聯絡人",
                tools: {
                    "Search contacts": {
                        name: "搜尋聯絡人",
                        description: "確認潛在客戶是否已存在。",
                    },
                    "Create contact": {
                        name: "建立聯絡人",
                        description: "把合格的潛在客戶加入 HubSpot。",
                    },
                },
            },
            "read-inbound-email-leads": {role: "讀取來信的潛在客戶", scope: "讀取來信的潛在客戶"},
        },
    },
    "crm-updater": {
        name: "CRM 更新",
        description: "依近期郵件往來更新 CRM 紀錄。",
        overview: "每天依您近期的郵件往來內容，更新 CRM 的聯絡人紀錄。",
        instructions: "每天檢查近期郵件往來，更新對應的 CRM 聯絡人紀錄。",
        trigger: "每天",
        triggerDescription: "每天依排程執行一次。",
        seedMessage: "建立一個代理：每天依我近期的郵件往來更新 CRM 聯絡人紀錄。",
        connections: {
            "read-the-threads": {
                role: "讀取郵件往來",
                scope: "讀取近期郵件往來",
                tools: {
                    "Fetch emails": {
                        name: "取得郵件",
                        description: "列出近期郵件，找出與 CRM 相關的更新。",
                    },
                    "Read thread": {
                        name: "讀取郵件串",
                        description: "讀取郵件內容以整理聯絡人更新。",
                    },
                },
            },
            "land-the-updates": {role: "寫入更新", scope: "寫入更新"},
        },
    },
    "outreach-drafter": {
        name: "開發信草擬",
        description: "為聯絡人清單草擬個人化的開發信。",
        overview: "為 CRM 清單上的每位聯絡人草擬個人化的開發信，供您審閱。",
        instructions: "依 CRM 聯絡人清單，為每位聯絡人草擬個人化的開發信。",
        trigger: "手動",
        triggerDescription: "您要求為某份清單草擬開發信時執行。",
        seedMessage: "建立一個代理：為 CRM 聯絡人清單草擬個人化的開發信。",
        connections: {
            "read-the-contact-list": {
                role: "讀取聯絡人清單",
                scope: "讀取聯絡人清單",
                tools: {
                    "List contacts": {name: "列出聯絡人", description: "取得目標聯絡人清單。"},
                    "Get contact": {
                        name: "取得聯絡人",
                        description: "讀取聯絡人資料以個人化草稿。",
                    },
                },
            },
            "hold-the-drafts": {role: "存放草稿", scope: "存放草稿"},
        },
    },
    "meeting-followup": {
        name: "會議後續追蹤",
        description: "草擬會後追蹤信，並把紀錄寫入 CRM。",
        overview: "每次會議後草擬追蹤信，並把會議紀錄寫入 CRM。",
        instructions: "會議結束後草擬追蹤信，並把紀錄寫在對應的 CRM 聯絡人上。",
        trigger: "會議結束",
        triggerDescription: "行事曆會議結束後執行，也可依排程執行。",
        seedMessage: "建立一個代理：每次會議後草擬追蹤信，並把紀錄寫入 CRM。",
        connections: {
            "draft-the-follow-up": {
                role: "草擬追蹤信",
                scope: "讀取會議紀錄、草擬追蹤信",
                tools: {
                    "Read thread": {name: "讀取郵件串", description: "讀取會議摘要或紀錄郵件。"},
                    "Create draft": {name: "建立草稿", description: "草擬寄給與會者的追蹤信。"},
                },
            },
            "read-the-meeting": {role: "讀取會議", scope: "讀取會議"},
            "update-the-crm": {role: "更新 CRM", scope: "更新 CRM"},
        },
    },
    "pipeline-digest": {
        name: "案源進度摘要",
        description: "每日摘要案源變化與停滯的交易。",
        overview: "每天把案源變化與停滯的交易整理成摘要，貼到 Slack。",
        instructions: "每天整理案源變化與停滯的交易，並張貼摘要。",
        trigger: "每天",
        triggerDescription: "每天依排程執行一次。",
        seedMessage: "建立一個代理：每天把案源變化與停滯的交易摘要貼到 Slack。",
        connections: {
            "read-the-deals": {
                role: "讀取交易",
                scope: "讀取交易",
                tools: {
                    "List deals": {name: "列出交易", description: "取得昨天以來有變化的交易。"},
                    "Get deal": {name: "取得交易", description: "確認交易停滯了多久。"},
                },
            },
            "post-the-digest": {
                role: "張貼摘要",
                scope: "張貼案源摘要",
                tools: {
                    "List channels": {name: "列出頻道", description: "找出要張貼的頻道。"},
                    "Send message": {name: "傳送訊息", description: "把案源摘要貼到頻道。"},
                },
            },
        },
    },
    "docs-qa": {
        name: "文件問答",
        description: "依您的文件工作區回答問題。",
        overview: "依您的文件工作區回答問題，答案簡潔並附出處；可在對話中或被提及時使用。",
        instructions: "搜尋文件工作區回答問題，回覆簡潔並附出處。",
        trigger: "被提及時",
        triggerDescription: "代理被 @ 提及時執行。",
        seedMessage: "建立一個文件問答代理：依我們的工作區回答問題並附出處。",
        connections: {
            "read-the-documentation": {
                role: "讀取文件",
                scope: "讀取頁面",
                tools: {
                    Search: NOTION_SEARCH,
                    "Get page content": {
                        name: "取得頁面內容",
                        description: "讀取頁面內容以草擬附出處的答案。",
                    },
                },
            },
            "read-an-extra-source": {role: "讀取額外來源", scope: "讀取額外來源"},
        },
    },
    "knowledge-chatbot": {
        name: "知識庫客服機器人",
        description: "依您的知識庫回答客戶問題。",
        overview: "依您的知識庫回答客戶問題，可在對話中或被提及時使用。",
        instructions: "搜尋知識庫回答客戶問題，回覆簡潔並附出處。",
        trigger: "被提及或有新訊息",
        triggerDescription: "被提及或收到客戶新訊息時執行。",
        seedMessage: "建立一個面向客戶的機器人：依我們的知識庫回答問題。",
        connections: {
            "read-the-knowledge-base": {
                role: "讀取知識庫",
                scope: "讀取頁面",
                tools: {
                    Search: NOTION_SEARCH,
                    "Get page content": {
                        name: "取得頁面內容",
                        description: "讀取頁面以草擬附出處的答案。",
                    },
                },
            },
            "reply-to-the-asker": {
                role: "回覆提問者",
                scope: "回覆客戶問題",
                tools: {
                    "Fetch conversation history": {
                        name: "讀取對話紀錄",
                        description: "讀取提問的討論串。",
                    },
                    "Send message": {name: "傳送訊息", description: "在討論串中回覆附出處的答案。"},
                },
            },
        },
    },
    "onboarding-buddy": {
        name: "新進人員小幫手",
        description: "依內部知識庫回答新進人員的問題。",
        overview: "被提及時搜尋內部知識庫，回答新進人員的問題。",
        instructions: "被提及時搜尋內部知識庫，回答新進人員的問題。",
        trigger: "被提及時",
        triggerDescription: "代理被 @ 提及時執行。",
        seedMessage: "建立一個新進人員小幫手：依我們的內部知識庫回答新進人員的問題。",
        connections: {
            "read-the-onboarding-material": {
                role: "讀取新進人員資料",
                scope: "讀取頁面",
                tools: {
                    Search: {name: "搜尋", description: "找出與問題相關的知識庫頁面。"},
                    "Get page content": {name: "取得頁面內容", description: "讀取頁面以正確回答。"},
                },
            },
            "reply-to-the-new-starter": {
                role: "回覆新進人員",
                scope: "在討論串中回覆 @ 提及",
                tools: {
                    "Fetch conversation history": {
                        name: "讀取對話紀錄",
                        description: "讀取新進人員提及的討論串。",
                    },
                    "Send message": {name: "傳送訊息", description: "在討論串中回覆附出處的答案。"},
                },
            },
        },
    },
    "content-repurposer": {
        name: "內容改寫",
        description: "把已發布的文章改寫成社群貼文草稿。",
        overview: "把已發布的文章改寫成 LinkedIn 與 X 貼文草稿，供您審閱。",
        instructions: "依已發布的文章內容，草擬一則 LinkedIn 貼文與一則 X 貼文。",
        trigger: "手動",
        triggerDescription: "您指定要改寫的文章時執行。",
        seedMessage: "建立一個代理：把已發布的文章改寫成 LinkedIn 與 X 貼文草稿供審閱。",
        connections: {
            "read-the-source-and-hold-drafts": {
                role: "讀取來源並存放草稿",
                scope: "讀取與建立頁面",
                tools: {
                    "Get page content": {name: "取得頁面內容", description: "讀取已發布的文章。"},
                    "Create page": {name: "建立頁面", description: "儲存草稿供審閱。"},
                },
            },
            "read-a-swappable-source": {role: "讀取替代來源", scope: "讀取替代來源"},
            "review-the-drafts": {role: "審閱草稿", scope: "審閱草稿"},
        },
    },
    "standup-summarizer": {
        name: "每日站會摘要",
        description: "每天張貼頻道活動摘要。",
        overview: "每天張貼頻道活動摘要，讓團隊一早就有清楚的站會重點。",
        instructions: "每天早上把昨天的頻道活動整理成簡短的站會摘要並張貼。",
        trigger: "每天 09:00",
        triggerDescription: "每天 09:00 執行。",
        seedMessage: "建立一個代理：每天張貼昨天頻道活動的站會摘要。",
        connections: {
            "read-and-post-to-the-channel": {
                role: "讀取並張貼到頻道",
                scope: "讀取頻道、張貼摘要",
                tools: {
                    "Fetch conversation history": {
                        name: "讀取對話紀錄",
                        description: "讀取追蹤頻道中昨天的訊息。",
                    },
                    "Send message": {name: "傳送訊息", description: "張貼站會摘要。"},
                },
            },
        },
    },
    "job-hunt-research": {
        name: "求職研究助理",
        description: "找出合適的職缺，並研究最符合條件的公司。",
        overview:
            "依您的個人檔案建立經過查證的職缺清單、研究雇主，並儲存有依據的適配說明與下一步。",
        instructions: "找出符合您個人檔案的現有職缺，逐一查證，並研究最合適的幾家。",
        trigger: "隨時",
        triggerDescription: "需要新的職缺清單時手動執行。",
        seedMessage:
            "建立一個求職研究助理：找出符合條件的職缺、查證後研究公司，並儲存附日期的報告。",
        example: {
            prompt: "找出台北目前符合我條件的法務專員職缺。",
            steps: [
                "依儲存的職務、地點與技能條件比對職缺",
                "確認地點、刊登時間與是否仍在招募",
                "研究雇主並記錄有出處的適配說明",
                "儲存附日期的報告，含連結與注意事項",
            ],
            reply: "職缺清單已完成，附應徵連結、有依據的適配說明與下一步。未經查證的資訊會標明，不會猜測。",
            artifacts: ["job-hunt/reports/<date>.md"],
            status: "報告已儲存",
        },
        connections: {
            telegram: {
                role: "只把排程的求職摘要與報告連結傳到使用者確認過的私人對話。",
                scope: "連線您自己的 Telegram 帳號，並在啟用排程傳送前確認私人對話。不得用來聯繫雇主。",
                tools: {
                    "Find direct-chat updates": {
                        name: "找出私人對話",
                        description: "檢查 Telegram 更新，確認使用者的私人對話。",
                    },
                    "Send scheduled report": {
                        name: "傳送排程報告",
                        description: "把求職摘要與報告連結傳到使用者確認過的對話。",
                    },
                },
            },
        },
    },
    "seo-content-agent": {
        name: "SEO 內容代理",
        description: "研究搜尋機會，並把核准的大綱寫成有用的內容。",
        overview:
            "一位 SEO 同事，具備研究、規劃、寫作與審稿等八項技能，有整潔的編輯工作區，大綱與發布前各自需要核准。",
        instructions:
            "了解網站、調查搜尋需求、建議小規模的內容計畫，並把核准的大綱寫成經審閱的草稿。證據與負責人的決定都與工作一起保存。",
        trigger: "隨時",
        triggerDescription: "在對話中開始，或送出已儲存的編輯工作區請求。預設不啟用任何排程。",
        seedMessage:
            "建立一個 SEO 內容代理：研究我的網站、建議小規模的內容計畫，並草擬核准的大綱。",
        example: {
            prompt: "調查我網站的 SEO，並提出小規模的起步內容計畫。",
            steps: [
                "讀取現有資料並研究代表性頁面",
                "蒐集可取得的搜尋需求與競爭對手證據",
                "建議聚焦的計畫並說明證據的限制",
                "儲存報告，並在撰寫大綱前請您回饋",
            ],
            reply: "第一份調查已完成，區分了觀察到的證據與假設，並建議小規模的起步計畫供您審閱。尚未草擬或發布任何文章。",
            artifacts: ["agent-files/01-research/<date>/report.md"],
            status: "等待方向回饋",
        },
        connections: {
            "composio-search": {
                role: "找出公開證據、競爭對手與搜尋機會。",
                scope: "選擇您自己的可用連線，第一次使用時確認權限。不含任何帳號綁定。",
                tools: {
                    "Search the web": {
                        name: "搜尋網路",
                        description: "找出公開證據、競爭對手與受眾問題。",
                    },
                },
            },
            "context-dev": {
                role: "讀取網頁以進行研究與事實查核。",
                scope: "選擇您自己的可用連線，第一次使用時確認權限。不含任何帳號綁定。",
                tools: {
                    "Read webpages": {
                        name: "讀取網頁",
                        description: "讀取來源以進行研究與事實查核。",
                    },
                },
            },
            "peec-ai-mcp": {
                role: "檢視您選定專案在 AI 搜尋中的能見度。",
                scope: "選擇您自己的可用連線，第一次使用時確認權限。不含任何帳號綁定。",
                tools: {
                    "Review AI visibility": {
                        name: "檢視 AI 能見度",
                        description: "檢查您自己專案的能見度。",
                    },
                },
            },
            "fal-ai": {
                role: "依您的要求產生封面圖片。",
                scope: "選擇您自己的可用連線，第一次使用時確認權限。不含任何帳號綁定。",
                tools: {
                    "Generate images": {
                        name: "產生圖片",
                        description: "依您的品牌指引產生封面素材。",
                    },
                },
            },
            "google-search-console": {
                role: "檢查您網站的搜尋成效與索引狀態。",
                scope: "選擇您自己的帳號與網站資源，使用資料前先確認權限。",
                tools: {
                    "Inspect search evidence": {
                        name: "檢查搜尋資料",
                        description: "讀取您網站資源的成效與索引資料。",
                    },
                },
            },
            "browserbase-mcp": {
                role: "在研究與審閱時檢查渲染後的網頁並保存證據。",
                scope: "選擇您自己的瀏覽器服務帳號，第一次使用時先做一次有限的瀏覽確認。",
                tools: {
                    "Inspect rendered pages": {
                        name: "檢查渲染網頁",
                        description: "在瀏覽器中檢查網頁並保存研究證據。",
                    },
                },
            },
        },
    },
}

/** "2 Slack + 1 Linear tools" → "Slack 2 個、Linear 1 個工具". */
const zhToolsSummary = (summary: string): string => {
    const match = summary.match(/^(.*) tools?$/)
    if (!match) return summary
    const parts = match[1].split(" + ").map((part) => {
        const [count, ...name] = part.split(" ")
        return name.length ? `${name.join(" ")} ${count} 個` : part
    })
    return `${parts.join("、")}工具`
}

/** The template's display text in `locale`; unknown locales and keys keep the catalog text. */
export const localizeAgentTemplate = (
    template: AgentStarterTemplate,
    locale: string,
): AgentStarterTemplate => {
    if (locale !== "zh-TW") return template
    const copy = ZH_TW[template.key]
    const toolsSummary = zhToolsSummary(template.toolsSummary)
    if (!copy) return {...template, toolsSummary}
    return {
        ...template,
        name: copy.name,
        description: copy.description,
        overview: copy.overview,
        instructions: copy.instructions,
        toolsSummary,
        trigger: copy.trigger,
        triggerDescription: copy.triggerDescription,
        seedMessage: copy.seedMessage,
        builderMessage: copy.seedMessage,
        ...(copy.example ? {example: copy.example} : {}),
        connections: template.connections.map((connection) => {
            const slot = copy.connections?.[connection.key]
            if (!slot) return connection
            return {
                ...connection,
                role: slot.role ?? connection.role,
                primary: {
                    ...connection.primary,
                    ...(slot.scope ? {scope: slot.scope} : {}),
                    ...(connection.primary.tools
                        ? {
                              tools: connection.primary.tools.map(
                                  (tool) => slot.tools?.[tool.name] ?? tool,
                              ),
                          }
                        : {}),
                },
            }
        }),
    }
}
